// Library methods
import { useCallback, useMemo } from "react";

// Components
import Skeleton from "@mui/material/Skeleton";
import Dash from "src/assets/shapes/Dash/Dash";
import Circle from "src/assets/shapes/Circle/Circle";
import ColoredCircle from "src/assets/shapes/ColoredCircle/ColoredCircle";

// Styles
import styles from "./totalDevGraph.module.css";

// Utils
import { getVisualFieldGrid } from "src/utils/exams/numberChartHelper";
import { getDeviationChartPattern } from "src/utils/exams/examReportsHelper";

// Constants
import { eyeTypeEnums, gridTypeEnums } from "src/constants/enums";

// Types
import { ChartSpotType } from "src/services/exams/types";

const DEFAULT_CONTAINER_SIZE = 252;

type TotalDevGraphType = {
  pressedPoints: Array<ChartSpotType>;
  eyeType: string;
  gridType: string | undefined;
  isLiveUpdate?: boolean;
  containerSize?: number;
};

const TotalDevGraph = ({
  pressedPoints,
  eyeType,
  gridType,
  isLiveUpdate = false,
  containerSize = DEFAULT_CONTAINER_SIZE,
}: TotalDevGraphType) => {
  const { LEFT, RIGHT } = eyeTypeEnums;

  const size = useMemo(() => containerSize * 0.9, [containerSize]);

  const scaleFactor = useMemo(
    () => containerSize / DEFAULT_CONTAINER_SIZE,
    [containerSize]
  );

  const getImage = (valuePercentile: number | undefined) => {
    const pattern = getDeviationChartPattern(valuePercentile);
    if (!pattern)
      return (
        <div
          style={{
            width: 12,
            height: 6,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "16px",
          }}
        >
          .
        </div>
      );

    return (
      <img
        src={pattern}
        alt="Total deviation value representation"
        className={styles.imageRender}
        style={{ width: 12, height: 12, objectFit: "fill" }}
      />
    );
  };

  const allExamPoints = useMemo(
    () => getVisualFieldGrid(gridType, eyeType),
    [gridType, eyeType]
  );

  const shouldIgnoreValue = useCallback(
    (spot: { x: number; y: number }) => {
      return (
        gridType !== gridTypeEnums.G_10_2.type &&
        ((eyeType === LEFT && spot.x === -3 && spot.y === -1) ||
          (eyeType === LEFT && spot.x === -3 && spot.y === 1) ||
          (eyeType === RIGHT && spot.x === 3 && spot.y === -1) ||
          (eyeType === RIGHT && spot.x === 3 && spot.y === 1))
      );
    },
    [eyeType, gridType, LEFT, RIGHT]
  );

  const calculateXPaddingTotalDevGraph = useCallback((xFloor: number) => {
    // jumpAxis lets us jump from negative x axis to positive x axis, 152 (half of total width + line width + 5px padding)
    const jumpAxis = 105;

    if (xFloor <= 0) {
      return 120 - xFloor * -23;
    } else {
      return xFloor * 23 + jumpAxis;
    }
  }, []);

  const calculateYPaddingTotalDevGraph = useCallback((yFloor: number) => {
    // jumpAxis lets us jump from positive y axis to negative y axis, 152 (half of total height + line width + 5px padding)
    const jumpAxis = 100;

    if (yFloor >= 0) {
      return 118 - yFloor * 25;
    } else {
      return jumpAxis + yFloor * -25;
    }
  }, []);

  const getPaddingLeft = useCallback(
    (ml: number) => (ml - 4) * scaleFactor,
    [scaleFactor]
  );

  const getPaddingTop = useCallback(
    (mt: number) => mt * scaleFactor,
    [scaleFactor]
  );

  // Show loading skeleton while data is not yet processed
  if (pressedPoints.length === 0) {
    return (
      <Skeleton
        variant="rounded"
        width="100%"
        height={DEFAULT_CONTAINER_SIZE}
        sx={{
          borderRadius: "4px",
        }}
      />
    );
  }

  return (
    <main
      style={{
        height: `${size}px`,
        width: `${size}px`,
        position: "relative",
      }}
    >
      {/* Vertical Line */}
      <div
        style={{
          position: "absolute",
          borderRight: "2px solid #000",
          height: `${size}px`,
          marginLeft: `${size / 2}px`,
        }}
      />

      {/* Horizontal Line */}
      <div
        style={{
          position: "absolute",
          borderBottom: "2px solid #000",
          width: `${size}px`,
          marginTop: `${size / 2}px`,
        }}
      />

      {allExamPoints.map((spot, i: number) => {
        const hasValue = pressedPoints
          .filter((point) => point.decibelValue !== null) // disconsider the points that has a decibelValue equal to null
          .find((point) => point.x === spot.x && point.y === spot.y);

        const ml = calculateXPaddingTotalDevGraph(spot.x);
        const mt = calculateYPaddingTotalDevGraph(spot.y);

        if (hasValue) return null;

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              paddingLeft: getPaddingLeft(ml),
              paddingTop: getPaddingTop(mt),
            }}
          >
            <Circle size={12} stroke="#FFF" />
          </div>
        );
      })}

      {pressedPoints
        .filter((spot) => !spot.completed && spot.decibelValue !== null) // considering the non completed ones, with a valid decibelValue/intensity
        .map((spot, i) => {
          const ml = calculateXPaddingTotalDevGraph(spot.x);
          const mt = calculateYPaddingTotalDevGraph(spot.y);

          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: getPaddingLeft(ml),
                paddingTop: getPaddingTop(mt),
              }}
            >
              {!isLiveUpdate ? (
                <Dash size={12} />
              ) : (
                <ColoredCircle size={12} isOrange={!spot.current} />
              )}
            </div>
          );
        })}

      {pressedPoints
        .filter((spot) => spot.completed)
        .map((spot, i) => {
          const shouldIgnore = shouldIgnoreValue(spot);

          const ml = calculateXPaddingTotalDevGraph(spot.x);
          const mt = calculateYPaddingTotalDevGraph(spot.y);

          if (shouldIgnore) return null;

          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: getPaddingLeft(ml),
                paddingTop: getPaddingTop(mt),
              }}
            >
              {getImage(spot.valuePercentile)}
            </div>
          );
        })}
    </main>
  );
};

export default TotalDevGraph;
